import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import classnames from 'classnames';
import { useAsync, useMedia } from 'react-use';
import { Button, Select, Typography, Loading } from 'ck-ui';
import { getStoreLocation } from 'ck-utils';
import styles from './CareerBlock.module.scss';

const CareerBlock = ({ pageSize }) => {
  const [setting, setSetting] = useState({
    currentCountry: '',
    currentLocation: '',
    currentTeam: '',
    pageSize
  });
  const isTouch = useMedia('(max-width: 1087px)');
  const [jobList, setJobList] = useState(null);
  const storeLocation = getStoreLocation();

  useAsync(async () => {
    const { data } = await axios.get('https://api.lever.co/v0/postings/culturekings?mode=json');
    setJobList(data);
    setSetting((prev) => ({
      ...prev,
      currentCountry: data.find((item) => item?.categories?.department?.includes(storeLocation))?.categories?.department || ''
    }));
  }, []);

  if (!jobList) return <Loading isLoading />;

  const { currentCountry, currentLocation, currentTeam } = setting;

  const result = jobList.reduce((acc, item) => {
    const { filteredJobList, countriesOptions, locationsOptions, teamsOptions } = acc;
    const { department, location, team } = item?.categories || {};
    const countryCondition = !currentCountry || department === currentCountry;
    if (!countriesOptions.includes(department)) {
      countriesOptions.push(department);
    }
    if (!locationsOptions.includes(location) && countryCondition) {
      locationsOptions.push(location);
    }

    if (!teamsOptions.includes(team) && countryCondition) {
      teamsOptions.push(team);
    }

    if (countryCondition
      && (!currentLocation || currentLocation === location)
      && (!currentTeam || currentTeam === team)) {
      filteredJobList.push(item);
    }
    return {
      filteredJobList, countriesOptions, locationsOptions, teamsOptions
    };
  }, { filteredJobList: [], countriesOptions: [], locationsOptions: [], teamsOptions: [] });

  const {
    filteredJobList, countriesOptions, locationsOptions, teamsOptions
  } = result;

  const getFlag = (country) => {
    if (country.includes('NZ')) {
      return '🇳🇿';
    }
    if (country.includes('US')) {
      return '🇺🇸';
    }
    return '🇦🇺';
  };

  return (
    <section className="home-section">
      <div className="container">
        <div className={classnames(styles.card, styles.sel)}>
          <Select
            size="large"
            fullwidth
            value={currentCountry}
            onChange={(e) => {
              setSetting((prev) => ({
                ...prev,
                currentLocation: '',
                currentCountry: e.target.value,
                currentTeam: '',
                pageSize
              }));
            }}
          >
            <option
              key="all"
              value=""
            >Select your country...
            </option>
            {countriesOptions.map((country) => (
              <option
                key={country}
                value={country}
              >
                {country}
              </option>
            ))}
          </Select>
          <Select
            size="large"
            fullwidth
            value={currentLocation}
            onChange={(e) => {
              setSetting((prev) => ({
                ...prev,
                currentLocation: e.target.value,
                pageSize
              }));
            }}
          >
            <option
              key="all"
              value=""
            >Select your location...
            </option>
            {locationsOptions.map((location) => (
              <option
                key={location}
                value={location}
              >
                {location}
              </option>
            ))}
          </Select>
          <Select
            size="large"
            fullwidth
            value={currentTeam}
            onChange={(e) => {
              setSetting((prev) => ({
                ...prev,
                currentTeam: e.target.value,
                pageSize
              }));
            }}
          >
            <option
              key="all"
              value=""
            >Select your team...
            </option>
            {teamsOptions?.map((team) => (
              <option
                key={team}
                value={team}
              >
                {team}
              </option>
            ))}
          </Select>
        </div>

        <div className={styles.jobRoot}>
          {filteredJobList.length === 0 ? (
            <div className={classnames(styles.card, styles.width)}>
              <Typography display="block" variant={isTouch ? 'heading6' : 'heading5'} className={classnames(styles.heading, 'no-divider')}>
                No job postings match these filters.
              </Typography>
            </div>
          )
            : (
              filteredJobList.slice(0, setting.pageSize).map((item) => (
                <a
                  key={item.id}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classnames(styles.card, styles.width)}
                  href={item?.hostedUrl}
                  title={item?.categories?.team}
                >
                  {item?.categories?.team && (
                    <Typography uppercase display="inline" variant={isTouch ? 'heading10' : 'heading8'} className={styles.team}>
                      {item?.categories?.team}
                    </Typography>
                  )}
                  {item?.categories?.commitment && (
                    <Typography display="inline" variant={isTouch ? 'body-small' : 'body-medium'} className={styles.grey}>
                      {item?.categories?.commitment}
                    </Typography>
                  )}
                  {item?.text && (
                    <Typography display="block" variant={isTouch ? 'heading6' : 'heading5'} className={classnames(styles.heading, 'no-divider')}>
                      {item?.text}
                    </Typography>
                  )}
                  <span
                    className={styles.emoji}
                    role="img"
                    aria-label={item?.categories?.department}
                  >
                    {getFlag(item?.categories?.department)}
                  </span>

                  {item?.categories?.location && (
                    <Typography display="inline" variant={isTouch ? 'body-small' : 'body-medium'} className={styles.grey}>
                      {item?.categories?.location}
                    </Typography>
                  )}
                </a>
              ))
            )
          }

          {setting.pageSize < filteredJobList.length
              && (
                <Button
                  size={isTouch ? 'medium' : 'large'}
                  onClick={() => {
                    setSetting((prev) => ({
                      ...prev,
                      pageSize: prev.pageSize + pageSize,
                    }));
                  }}
                >Load More
                </Button>
              )
          }
        </div>

      </div>
    </section>
  );
};

CareerBlock.propTypes = {
  pageSize: PropTypes.number.isRequired
};

export { CareerBlock };
